import { once } from 'lodash';

import { STATE_TAX_ITEMS } from '@/modules/education/Education.constants';
import { PresentationBundleKind, PresentationPageKind } from '@/types/schema';

import { BUSINESSES_OVERVIEW_DEFAULT_ITEMS_PER_PAGE } from '../../ClientPresentationDesigner.constants';
import { ClientPresentationV2Bundle } from '../../types/ClientPresentation.PresentationBundleType';
import { getEducationalPageDefaultDisplayNames } from '../slides/EducationalSlide/EducationalSlide.utils';

/**
 * Label shown for the beneficiaries/dispositions page when the entity uses
 * beneficiary designations (retirement / DH-insurance accounts) instead of
 * dispositions. Replaces the default "Current beneficiaries and dispositions".
 */
export const BENEFICIARY_DESIGNATIONS_PAGE_LABEL = 'Beneficiary designations';

type PaginatedConfigurationField =
  | 'trustOverviewBundleConfiguration'
  | 'businessOverviewBundleConfiguration'
  | 'assetDistributionBundleConfiguration'
  | 'willBundleConfiguration'
  | 'powerOfAttorneyBundleConfiguration'
  | 'healthcareDirectiveBundleConfiguration';

export const ITEMS_PER_PAGE_SETTINGS: Partial<
  Record<
    PresentationBundleKind,
    {
      field: PaginatedConfigurationField;
      options: readonly number[];
      /** Value shown when the configuration has no `itemsPerPage`.
       *  Defaults to the largest option. */
      defaultOption?: number;
    }
  >
> = {
  [PresentationBundleKind.TrustOverviewBundle]: {
    field: 'trustOverviewBundleConfiguration',
    options: [1, 2, 3, 4],
  },
  [PresentationBundleKind.BusinessOverviewBundle]: {
    field: 'businessOverviewBundleConfiguration',
    options: [1, 2, 3, 4],
    defaultOption: BUSINESSES_OVERVIEW_DEFAULT_ITEMS_PER_PAGE,
  },
  [PresentationBundleKind.AssetDistributionBundle]: {
    field: 'assetDistributionBundleConfiguration',
    options: [1, 2, 3, 4],
  },
  [PresentationBundleKind.WillBundle]: {
    field: 'willBundleConfiguration',
    options: [1, 2],
  },
  [PresentationBundleKind.PowerOfAttorneyBundle]: {
    field: 'powerOfAttorneyBundleConfiguration',
    options: [1, 2],
  },
  [PresentationBundleKind.HealthcareDirectiveBundle]: {
    field: 'healthcareDirectiveBundleConfiguration',
    options: [1, 2],
  },
};

export const getPageDisplayNames = once(
  (): Record<PresentationPageKind, string> => ({
    [PresentationPageKind.CoverPage]: 'Cover page',
    [PresentationPageKind.CustomPage]: 'Custom page',
    [PresentationPageKind.DiagramPage]: 'Custom diagram',
    [PresentationPageKind.Divider]: 'Divider page',
    [PresentationPageKind.PresentationBundleCoverPage]: 'Cover',
    [PresentationPageKind.TableOfContentsPage]: 'Table of contents',
    [PresentationPageKind.DocumentSummaryPage]: 'Document summary',
    [PresentationPageKind.DocumentsReviewedPage]: 'Documents reviewed',
    [PresentationPageKind.ProfessionalTeamPage]: 'Professional team',
    // [PresentationPageKind.ValueCreationPage]: 'Value creation',
    [PresentationPageKind.WaterfallCoverPage]: 'Cover',
    [PresentationPageKind.WaterfallAssumptionsPage]: 'Assumptions',
    [PresentationPageKind.WaterfallSummaryPage]: 'Summary',
    [PresentationPageKind.WaterfallDiagramPage]: 'Diagram',
    [PresentationPageKind.WaterfallBeneficiariesPage]: 'Beneficiaries',
    [PresentationPageKind.WaterfallTaxOverviewPage]: 'Tax overview',
    [PresentationPageKind.WaterfallComparisonPage]: 'Comparison',
    [PresentationPageKind.WaterfallComparisonCoverPage]: 'Cover',
    [PresentationPageKind.WaterfallComparisonAssumptionsPage]: 'Assumptions',
    [PresentationPageKind.EntitiesSummaryPage]: 'Summary',
    [PresentationPageKind.EntitiesDiagramPage]: 'Diagram',
    [PresentationPageKind.EntityBeneficiariesAndDispositionsPage]:
      'Current beneficiaries and dispositions',
    [PresentationPageKind.EntityDiagramAndNotesPage]: 'Diagram',
    [PresentationPageKind.EntityBalanceSheetPage]: 'Balance sheet',
    [PresentationPageKind.EntityBalanceSheetExportPage]:
      'Balance sheet (full sheet)',
    [PresentationPageKind.EntityInsurancePolicyPage]: 'Insurance policies',
    [PresentationPageKind.EntitySummaryPage]: 'Cover',
    [PresentationPageKind.BalanceSheetPage]: 'Balance sheet',
    [PresentationPageKind.StandaloneDisclaimerPage]: 'Firm disclaimer',
    [PresentationPageKind.LuminaryDisclaimerPage]: 'Luminary disclaimer',
    [PresentationPageKind.ActiveEstateInsightsPage]: 'Active insights',
    [PresentationPageKind.CompletedEstateInsightsPage]: 'Completed insights',
    [PresentationPageKind.EntityGraphViewPage]: 'Ownership diagram',
    [PresentationPageKind.FamilyTreePage]: 'Family tree',
    [PresentationPageKind.MissingDocumentsPage]: 'Missing documents',
    [PresentationPageKind.BeneficiaryReportPage]: 'Beneficiary report',
    [PresentationPageKind.BeneficiaryReportBeneficiary]: 'Beneficiary',
    [PresentationPageKind.BeneficiaryReportHousehold]: 'Overview',
    [PresentationPageKind.LiabilitiesPage]: 'Liabilities',
    [PresentationPageKind.LifeInsurancePage]: 'Life insurance',
    [PresentationPageKind.BusinessOverviewPage]: 'Businesses overview',
    [PresentationPageKind.TrustOverviewPage]: 'Trusts overview',
    [PresentationPageKind.FamilyOverviewPage]: 'Family overview',
    [PresentationPageKind.WillPage]: 'Wills',
    [PresentationPageKind.AssetDistributionPage]: 'Asset distribution',
    [PresentationPageKind.HealthcareDirectivePage]: 'Healthcare directive',
    [PresentationPageKind.PlanningOpportunitiesPage]:
      'Observations & opportunities',
    [PresentationPageKind.PowerOfAttorneyPage]: 'Power of attorney',
    // TODO: add display names when these pages are implemented
    [PresentationPageKind.BalanceSheetExpandedCategoriesPage]:
      'Expanded categories',
    [PresentationPageKind.BalanceSheetExportPage]: 'Full balance sheet',
    ...getEducationalPageDefaultDisplayNames(),
  })
);

/**
 * Gets bundle display names
 */
export function getBundleDisplayNames(): Record<
  PresentationBundleKind,
  string
> {
  return {
    [PresentationBundleKind.TableOfContentsBundle]: 'Table of contents',
    [PresentationBundleKind.BalanceSheetBundle]: 'Balance sheet',
    [PresentationBundleKind.EntitiesOverviewBundle]: 'Entities list',
    [PresentationBundleKind.WaterfallOverviewBundle]: 'Waterfall overview',
    [PresentationBundleKind.WaterfallComparisonBundle]: 'Waterfall comparison',
    [PresentationBundleKind.EntitySummaryBundle]: 'Entity overview',
    [PresentationBundleKind.CustomPageBundle]: 'Custom page',
    [PresentationBundleKind.DiagramBundle]: 'Asset distribution diagram',
    [PresentationBundleKind.CoverSlideBundle]: 'Cover',
    [PresentationBundleKind.ProfessionalTeamBundle]: 'Professional team',
    [PresentationBundleKind.StandaloneDisclaimerBundle]: 'Firm disclaimer',
    [PresentationBundleKind.LuminaryDisclaimerBundle]: 'Luminary disclaimer',
    [PresentationBundleKind.EducationPageBundle]: '', // this is never used; we use the title of the underlying educational page
    [PresentationBundleKind.DividerBundle]: 'Divider page',
    [PresentationBundleKind.DocumentSummaryBundle]: 'Document summary',
    [PresentationBundleKind.DocumentsReviewedBundle]: 'Documents reviewed',
    [PresentationBundleKind.EstateInsightsBundle]: 'Insights',
    [PresentationBundleKind.EntityGraphViewBundle]: 'Ownership diagram',
    [PresentationBundleKind.FamilyTreeBundle]: 'Family tree',
    [PresentationBundleKind.BeneficiaryReportBundle]: 'Beneficiary report',
    [PresentationBundleKind.LiabilitiesBundle]: 'Liabilities',
    [PresentationBundleKind.LifeInsuranceBundle]: 'Life insurance',
    [PresentationBundleKind.BusinessOverviewBundle]: 'Businesses overview',
    [PresentationBundleKind.TrustOverviewBundle]: 'Trusts overview',
    [PresentationBundleKind.FamilyOverviewBundle]: 'Family overview',
    [PresentationBundleKind.WillBundle]: 'Wills',
    [PresentationBundleKind.AssetDistributionBundle]: 'Asset distribution',
    [PresentationBundleKind.HealthcareDirectiveBundle]: 'Healthcare directives',
    [PresentationBundleKind.PlanningOpportunitiesBundle]:
      'Observations & opportunities',
    [PresentationBundleKind.PowerOfAttorneyBundle]: 'Powers of Attorney',
  };
}

// Legacy constant for backwards compatibility - will be removed once all usages are updated
export const BUNDLE_TO_DISPLAY_NAME: Record<PresentationBundleKind, string> =
  getBundleDisplayNames();

/**
 * Maps each E360 refresh-slide bundle kind to a function that reads the
 * user-edited slide title from its bundle-specific configuration object.
 * Consumers compare the result against `BUNDLE_TO_DISPLAY_NAME[type]` to
 * decide whether the title differs from the default (and is therefore
 * worth surfacing as nav subtext).
 */
export const REFRESH_BUNDLE_CONFIG_DISPLAY_NAME_GETTERS: Partial<
  Record<
    PresentationBundleKind | PresentationPageKind,
    (bundle: ClientPresentationV2Bundle) => string | null | undefined
  >
> = {
  [PresentationBundleKind.DocumentsReviewedBundle]: (b) =>
    b.documentsReviewedBundleConfiguration?.displayName,
  [PresentationBundleKind.TrustOverviewBundle]: (b) =>
    b.trustOverviewBundleConfiguration?.displayName,
  [PresentationBundleKind.BusinessOverviewBundle]: (b) =>
    b.businessOverviewBundleConfiguration?.displayName,
  [PresentationBundleKind.FamilyOverviewBundle]: (b) =>
    b.familyOverviewBundleConfiguration?.displayName,
  [PresentationBundleKind.WillBundle]: (b) =>
    b.willBundleConfiguration?.displayName,
  [PresentationBundleKind.PowerOfAttorneyBundle]: (b) =>
    b.powerOfAttorneyBundleConfiguration?.displayName,
  [PresentationBundleKind.HealthcareDirectiveBundle]: (b) =>
    b.healthcareDirectiveBundleConfiguration?.displayName,
  [PresentationBundleKind.PlanningOpportunitiesBundle]: (b) =>
    b.planningOpportunitiesBundleConfiguration?.displayName,
};

/**
 * Bundles in this list should not display child pages in the list;
 * however, they can be dragged into another parent.
 */
const SINGLE_PAGE_BUNDLES: PresentationBundleKind[] = [
  PresentationBundleKind.CoverSlideBundle,
  PresentationBundleKind.CustomPageBundle,
  PresentationBundleKind.DiagramBundle,
  PresentationBundleKind.TableOfContentsBundle,
  PresentationBundleKind.EntitiesOverviewBundle,
  PresentationBundleKind.ProfessionalTeamBundle,
  PresentationBundleKind.StandaloneDisclaimerBundle,
  PresentationBundleKind.LuminaryDisclaimerBundle,
  PresentationBundleKind.EducationPageBundle,
  PresentationBundleKind.DividerBundle,
  PresentationBundleKind.DocumentSummaryBundle,
  PresentationBundleKind.EntityGraphViewBundle,
  PresentationBundleKind.FamilyTreeBundle,
  PresentationBundleKind.LifeInsuranceBundle,
  PresentationBundleKind.LiabilitiesBundle,
  PresentationBundleKind.DocumentsReviewedBundle,
  PresentationBundleKind.TrustOverviewBundle,
  PresentationBundleKind.BusinessOverviewBundle,
  PresentationBundleKind.FamilyOverviewBundle,
  PresentationBundleKind.WillBundle,
  PresentationBundleKind.PowerOfAttorneyBundle,
  PresentationBundleKind.HealthcareDirectiveBundle,
  PresentationBundleKind.AssetDistributionBundle,
  PresentationBundleKind.PlanningOpportunitiesBundle,
];

export function isSinglePageBundle(
  type: PresentationBundleKind | PresentationPageKind | string | undefined
): boolean {
  return SINGLE_PAGE_BUNDLES.includes(type as PresentationBundleKind);
}

/**
 * When a page is dragged to root, create the mapped bundle type.
 *
 * If there is not a mapped bundle type, the page will not be eligible to be moved
 * to its own bundle.
 */
export const PAGE_TO_BUNDLE_TYPE_MAP: Partial<
  Record<PresentationPageKind, PresentationBundleKind>
> = {
  // Custom and Educational pages (already supported)
  [PresentationPageKind.CustomPage]: PresentationBundleKind.CustomPageBundle,

  // Educational pages → EducationPageBundle
  [PresentationPageKind.WhyEstatePlanningMatters]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.NoEstatePlanningConsequences]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.EstateTaxesExplainer]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.StateInheritanceTaxExplainer]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.ManagingRecentInheritance]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.PopularPlanningStrategies]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.WhyUseTrust]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.TrustBasics]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.EstatePlanningGlossary]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.SettingUpEstatePlan]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.EstatePlanningChecklist]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.GstExplainer]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.CltStrategyExplainer]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.CrtStrategyExplainer]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.GratStrategyExplainer]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.IlitStrategyExplainer]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.InstallmentSaleStrategyExplainer]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.SlatStrategyExplainer]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.QprtStrategyExplainer]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.FlpStrategyExplainer]:
    PresentationBundleKind.EducationPageBundle,
  [PresentationPageKind.PhilanthropicPlanningExplainer]:
    PresentationBundleKind.EducationPageBundle,
  ...Object.fromEntries(
    STATE_TAX_ITEMS.map((pageKind) => [
      pageKind,
      PresentationBundleKind.EducationPageBundle,
    ])
  ),

  // Divider page
  [PresentationPageKind.Divider]: PresentationBundleKind.DividerBundle,

  // Balance sheet pages
  [PresentationPageKind.BalanceSheetPage]:
    PresentationBundleKind.BalanceSheetBundle,
  [PresentationPageKind.BalanceSheetExportPage]:
    PresentationBundleKind.BalanceSheetBundle,

  // Core pages
  [PresentationPageKind.CoverPage]: PresentationBundleKind.CoverSlideBundle,
  [PresentationPageKind.TableOfContentsPage]:
    PresentationBundleKind.TableOfContentsBundle,
  [PresentationPageKind.EntitiesSummaryPage]:
    PresentationBundleKind.EntitiesOverviewBundle,
  [PresentationPageKind.ProfessionalTeamPage]:
    PresentationBundleKind.ProfessionalTeamBundle,

  // Disclaimer pages
  [PresentationPageKind.StandaloneDisclaimerPage]:
    PresentationBundleKind.StandaloneDisclaimerBundle,
  [PresentationPageKind.LuminaryDisclaimerPage]:
    PresentationBundleKind.LuminaryDisclaimerBundle,

  // Document pages
  [PresentationPageKind.DocumentSummaryPage]:
    PresentationBundleKind.DocumentSummaryBundle,
  [PresentationPageKind.MissingDocumentsPage]:
    PresentationBundleKind.DocumentSummaryBundle,

  // Entity pages → EntitySummaryBundle
  [PresentationPageKind.EntitySummaryPage]:
    PresentationBundleKind.EntitySummaryBundle,
  [PresentationPageKind.EntityDiagramAndNotesPage]:
    PresentationBundleKind.EntitySummaryBundle,
  [PresentationPageKind.EntityBalanceSheetPage]:
    PresentationBundleKind.EntitySummaryBundle,
  [PresentationPageKind.EntityBalanceSheetExportPage]:
    PresentationBundleKind.EntitySummaryBundle,
  [PresentationPageKind.EntityBeneficiariesAndDispositionsPage]:
    PresentationBundleKind.EntitySummaryBundle,
  [PresentationPageKind.EntityInsurancePolicyPage]:
    PresentationBundleKind.EntitySummaryBundle,
  [PresentationPageKind.EntitiesDiagramPage]:
    PresentationBundleKind.EntitySummaryBundle,
  [PresentationPageKind.EntityGraphViewPage]:
    PresentationBundleKind.EntityGraphViewBundle,

  // Family Tree page
  [PresentationPageKind.FamilyTreePage]:
    PresentationBundleKind.FamilyTreeBundle,

  // Life Insurance page
  [PresentationPageKind.LifeInsurancePage]:
    PresentationBundleKind.LifeInsuranceBundle,

  // Liabilities page
  [PresentationPageKind.LiabilitiesPage]:
    PresentationBundleKind.LiabilitiesBundle,

  // Estate Insights pages
  [PresentationPageKind.ActiveEstateInsightsPage]:
    PresentationBundleKind.EstateInsightsBundle,
  [PresentationPageKind.CompletedEstateInsightsPage]:
    PresentationBundleKind.EstateInsightsBundle,

  // Waterfall pages → WaterfallOverviewBundle
  [PresentationPageKind.WaterfallSummaryPage]:
    PresentationBundleKind.WaterfallOverviewBundle,
  [PresentationPageKind.WaterfallDiagramPage]:
    PresentationBundleKind.WaterfallOverviewBundle,
  [PresentationPageKind.WaterfallCoverPage]:
    PresentationBundleKind.WaterfallOverviewBundle,
  [PresentationPageKind.WaterfallTaxOverviewPage]:
    PresentationBundleKind.WaterfallOverviewBundle,
  [PresentationPageKind.WaterfallAssumptionsPage]:
    PresentationBundleKind.WaterfallOverviewBundle,
  [PresentationPageKind.WaterfallBeneficiariesPage]:
    PresentationBundleKind.WaterfallOverviewBundle,

  // Waterfall Comparison pages
  [PresentationPageKind.WaterfallComparisonPage]:
    PresentationBundleKind.WaterfallComparisonBundle,
  [PresentationPageKind.WaterfallComparisonCoverPage]:
    PresentationBundleKind.WaterfallComparisonBundle,
  [PresentationPageKind.WaterfallComparisonAssumptionsPage]:
    PresentationBundleKind.WaterfallComparisonBundle,

  // Other pages - these map to generic bundle types as fallbacks
  [PresentationPageKind.PresentationBundleCoverPage]:
    PresentationBundleKind.CoverSlideBundle,

  // Beneficiary report pages → BeneficiaryReportBundle
  [PresentationPageKind.BeneficiaryReportPage]:
    PresentationBundleKind.BeneficiaryReportBundle,
  [PresentationPageKind.BeneficiaryReportHousehold]:
    PresentationBundleKind.BeneficiaryReportBundle,
  [PresentationPageKind.BeneficiaryReportBeneficiary]:
    PresentationBundleKind.BeneficiaryReportBundle,
};

export const REPEATABLE_PAGE_TYPES: (
  | PresentationBundleKind
  | PresentationPageKind
)[] = [
  PresentationPageKind.BeneficiaryReportBeneficiary,
  PresentationPageKind.BalanceSheetPage,
  PresentationPageKind.BalanceSheetExportPage,
];
